import React, { useState, useContext, useEffect } from 'react';
import { NotificationContext } from './Dashboard';
import WalletLayout from './WalletLayout';
import SendDialog from './SendDialog';
import ReceiveDialog from './ReceiveDialog';
import SwapDialog from './SwapDialog';
import TransactionHistory from './TransactionHistory';
import VdexDetails from './VdexDetails';
import { Dialog } from '@mui/material';
import './Wallet.css';

const WalletLayoutPage = () => {
  const { showNotification } = useContext(NotificationContext);
  const [isLoading, setIsLoading] = useState(false);

  // Dialog states
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [showVdexDetails, setShowVdexDetails] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // State for testnet wallets
  const [testnetWallets, setTestnetWallets] = useState([]);

  // State for transaction history
  const [transactionHistory, setTransactionHistory] = useState([]);

  // Load transaction history from localStorage on component mount
  useEffect(() => {
    const loadTransactionHistory = () => {
      try {
        const savedTransactions = localStorage.getItem('transactionHistory');
        if (savedTransactions) {
          setTransactionHistory(JSON.parse(savedTransactions));
        } else {
          // Initialize with the recent transaction if no history exists
          const initialTransaction = {
            from: "0x15095Ec8FB1Fc9C664b3223459dFF43158ACe7aD",
            to: "0xc35bc2d01668e9234C3CD635350Aec1F3eC2DDC7",
            amount: "0.05",
            token: "ETH",
            type: "receive",
            timestamp: new Date().toISOString(),
            status: "completed",
            hash: "0xb0e93e9e48b6095775510ed5121f5862c96b330c7063c3cd5028fd7139f6c4f9"
          };
          setTransactionHistory([initialTransaction]);
          localStorage.setItem('transactionHistory', JSON.stringify([initialTransaction]));
        }
      } catch (error) {
        console.error("Error loading transaction history:", error);
      }
    };

    loadTransactionHistory();
  }, []);

  // Load wallets from localStorage on component mount
  useEffect(() => {
    try {
      const savedWallets = localStorage.getItem('testnetWallets');
      if (savedWallets) {
        const parsedWallets = JSON.parse(savedWallets);
        console.log('WalletLayoutPage: Loading wallets from localStorage:', parsedWallets);
        setTestnetWallets(parsedWallets);
      } else {
        // If no wallets in localStorage, use sample wallets
        setTestnetWallets([
    {
      name: "Rinkeby",
      icon: "🔷",
      address: "0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b",
      balance: "2.5 ETH",
      network: "Ethereum Testnet"
    },
    {
      name: "Sepolia",
      icon: "🔶",
      address: "0xc35bc2d01668e9234C3CD635350Aec1F3eC2DDC7",
      balance: "5.10 ETH",
      network: "Ethereum Testnet"
    },
    {
      name: "Goerli",
      icon: "🔹",
      address: "0x3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d",
      balance: "10.0 ETH",
      network: "Ethereum Testnet"
    },
    {
      name: "MetaMask",
      icon: "🦊",
      address: "0x4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e",
      balance: "0.75 ETH",
      network: "Mainnet"
    },
    {
      name: "Phantom",
      icon: "👻",
      address: "5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e",
      balance: "2.3 SOL",
      network: "Solana"
    },
    {
      name: "TON Wallet",
      icon: "💎",
      address: "UQBvP9XYQKi-lFfqaX2v4RLxdTRGgIjJ-lt-R8hEFQEjbhIB",
      balance: "15.5 TON",
      network: "The Open Network"
    }
        ]);
      }
    } catch (error) {
      console.error("Error loading wallets from localStorage:", error);
      // Fallback to empty array
      setTestnetWallets([]);
    }
  }, []);

  // Sample hardware wallets
  const hardwareWallets = [
    { name: 'Ledger', type: 'hardware' },
    { name: 'Trezor', type: 'hardware' },
    { name: 'KeepKey', type: 'hardware' }
  ];

  // Handle wallet actions
  const handleHotWalletConnect = (wallet) => {
    setIsLoading(true);
    setTimeout(() => {
      showNotification(`Connected to ${wallet ? wallet.name : 'MetaMask'} wallet`, 'success');
      setIsLoading(false);

      // Show the VdexDetails dialog with the connected wallet
      if (wallet) {
        setSelectedWallet(wallet);
        setShowVdexDetails(true);
      }
    }, 1500);
  };

  const handleColdWalletConnect = (wallet) => {
    setIsLoading(true);
    setTimeout(() => {
      showNotification(`Connected to ${wallet || 'Ledger'} hardware wallet`, 'success');
      setIsLoading(false);
    }, 1500);
  };

  const handleSend = (wallet) => {
    setSelectedWallet(wallet || testnetWallets[0]);
    setShowSendDialog(true);
  };

  const handleReceive = (wallet) => {
    // Make sure we have a valid wallet with an address
    if (wallet && wallet.address) {
      setSelectedWallet(wallet);
    } else if (testnetWallets && testnetWallets.length > 0) {
      setSelectedWallet(testnetWallets[0]);
    } else {
      console.warn('No valid wallet available for receive operation');
      // Show notification to user
      showNotification('Please connect a wallet first', 'warning');
      return; // Don't open dialog if no valid wallet
    }
    setShowReceiveDialog(true);
  };

  const handleSwap = (wallet) => {
    setSelectedWallet(wallet || testnetWallets[0]);
    setShowSwapDialog(true);
  };

  // Handle transaction completion
  const handleSendComplete = (transaction) => {
    console.log('Transaction completed:', transaction);
    showNotification(`Successfully sent ${transaction.amount} ${transaction.token} to ${transaction.to.substring(0, 8)}...`, 'success');
    setShowSendDialog(false);

    // Add transaction to history with timestamp
    const newTransaction = {
      ...transaction,
      type: 'send',
      timestamp: new Date().toISOString(),
      status: 'completed',
      hash: `0x${Math.random().toString(16).substring(2, 42)}`  // Generate a random hash for demo
    };

    // Update local state
    const updatedHistory = [newTransaction, ...transactionHistory];
    setTransactionHistory(updatedHistory);

    // Save to localStorage for persistence across components
    localStorage.setItem('transactionHistory', JSON.stringify(updatedHistory));

    // In a real app, we would update the wallet balance here
    // For now, we'll just simulate it
    setTimeout(() => {
      const updatedWallets = testnetWallets.map(wallet => {
        if (wallet.address === transaction.from) {
          const currentBalance = parseFloat(wallet.balance.split(' ')[0]);
          const newBalance = (currentBalance - parseFloat(transaction.amount)).toFixed(2);
          return {
            ...wallet,
            balance: `${newBalance} ${wallet.balance.split(' ')[1]}`
          };
        }
        return wallet;
      });

      handleWalletUpdate(updatedWallets);
    }, 1000);
  };

  const handleSwapComplete = (swap) => {
    console.log('Swap completed:', swap);
    showNotification(`Successfully swapped ${swap.fromAmount} ${swap.fromToken} for ${swap.toAmount} ${swap.toToken}`, 'success');
    setShowSwapDialog(false);

    // Add transaction to history with timestamp
    const newTransaction = {
      ...swap,
      type: 'swap',
      timestamp: new Date().toISOString(),
      status: 'completed',
      hash: `0x${Math.random().toString(16).substring(2, 42)}` // Generate a random hash for demo
    };

    // Update local state
    const updatedHistory = [newTransaction, ...transactionHistory];
    setTransactionHistory(updatedHistory);

    // Save to localStorage for persistence across components
    localStorage.setItem('transactionHistory', JSON.stringify(updatedHistory));

    // Update wallet balances
    setTimeout(() => {
      const updatedWallets = testnetWallets.map(wallet => {
        if (wallet.address === selectedWallet.address) {
          // Get current balance of the "from" token
          const currentFromBalance = parseFloat(wallet.balance.split(' ')[0]);
          const fromTokenSymbol = wallet.balance.split(' ')[1];

          // Only update if the wallet's token matches the from token
          if (fromTokenSymbol === swap.fromToken) {
            const newBalance = (currentFromBalance - parseFloat(swap.fromAmount)).toFixed(2);
            return {
              ...wallet,
              balance: `${newBalance} ${fromTokenSymbol}`
            };
          }
        }
        return wallet;
      });

      handleWalletUpdate(updatedWallets);
    }, 1000);
  };

  // Handle received funds
  const handleReceiveComplete = (transaction) => {
    console.log('Funds received:', transaction);
    showNotification(`Successfully received ${transaction.amount} ${transaction.token} from ${transaction.from.substring(0, 8)}...`, 'success');

    // Add transaction to history with timestamp
    const newTransaction = {
      ...transaction,
      type: 'receive',
      timestamp: new Date().toISOString(),
      status: 'completed'
    };

    // Update local state
    const updatedHistory = [newTransaction, ...transactionHistory];
    setTransactionHistory(updatedHistory);

    // Save to localStorage for persistence across components
    localStorage.setItem('transactionHistory', JSON.stringify(updatedHistory));

    // Update wallet balance
    setTimeout(() => {
      const updatedWallets = testnetWallets.map(wallet => {
        if (wallet.address.toLowerCase() === transaction.to.toLowerCase()) {
          const currentBalance = parseFloat(wallet.balance.split(' ')[0]);
          const newBalance = (currentBalance + parseFloat(transaction.amount)).toFixed(2);
          return {
            ...wallet,
            balance: `${newBalance} ${wallet.balance.split(' ')[1]}`
          };
        }
        return wallet;
      });

      handleWalletUpdate(updatedWallets);
    }, 1000);
  };

  // Function to handle wallet updates
  const handleWalletUpdate = (updatedWallets) => {
    console.log("Updating wallets in WalletLayoutPage:", updatedWallets);
    setTestnetWallets(updatedWallets);

    // Also update localStorage
    try {
      localStorage.setItem('testnetWallets', JSON.stringify(updatedWallets));
    } catch (error) {
      console.error("Error saving wallets to localStorage:", error);
      showNotification('Failed to save wallets to localStorage', 'error');
    }
  };

  return (
    <div className="wallet-container">
      <WalletLayout
        isHotWalletConnected={false}
        isColdWalletConnected={false}
        onHotWalletConnect={handleHotWalletConnect}
        onColdWalletConnect={handleColdWalletConnect}
        onSend={handleSend}
        onReceive={handleReceive}
        onSwap={handleSwap}
        testnetWallets={testnetWallets}
        hardwareWallets={hardwareWallets}
        setTestnetWallets={handleWalletUpdate}
      />

      {/* Transaction History */}
      {transactionHistory.length > 0 && (
        <TransactionHistory
          walletAddress={selectedWallet?.address || testnetWallets[0]?.address}
          transactions={transactionHistory}
        />
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Send Dialog */}
      <SendDialog
        open={showSendDialog}
        onClose={() => setShowSendDialog(false)}
        wallet={selectedWallet}
        onSend={handleSendComplete}
      />

      {/* Receive Dialog */}
      <ReceiveDialog
        open={showReceiveDialog}
        onClose={() => setShowReceiveDialog(false)}
        wallet={selectedWallet}
        onReceive={handleReceiveComplete}
      />

      {/* Swap Dialog */}
      <SwapDialog
        open={showSwapDialog}
        onClose={() => setShowSwapDialog(false)}
        wallet={selectedWallet}
        onSwap={handleSwapComplete}
      />

      {/* Vdex Details Dialog */}
      <Dialog
        open={showVdexDetails}
        onClose={() => setShowVdexDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <VdexDetails
          wallet={selectedWallet}
          onClose={() => setShowVdexDetails(false)}
          onSend={handleSend}
          onReceive={handleReceive}
          onSwap={handleSwap}
          onDisconnect={() => {
            setShowVdexDetails(false);
            showNotification('Wallet disconnected', 'info');
          }}
          testnetWallets={testnetWallets}
        />
      </Dialog>
    </div>
  );
};

export default WalletLayoutPage;
